import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  Snackbar,
  Alert,
  Typography,
  Paper,
  useTheme,
  useMediaQuery,
  Drawer,
  AppBar,
  Toolbar,
  IconButton,
  Button,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import {
  getLeadStages,
  createLeadStage,
  updateLeadStage,
  deleteLeadStage,
} from '../../api/leadStage.api.js';
import LeadStageList from './components/LeadStageList';
import LeadStageDetail from './components/LeadStageDetail';
import LeadStageForm from './components/LeadStageForm';
import LeadStageImport from './components/LeadStageImport';

function LeadStagesPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [leadStages, setLeadStages] = useState([]);
  const [selectedLeadStage, setSelectedLeadStage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [editingLeadStage, setEditingLeadStage] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  useEffect(() => {
    loadLeadStages();
  }, []);

  const loadLeadStages = async () => {
    setLoading(true);
    try {
      const data = await getLeadStages();
      setLeadStages(data);
    } catch (error) {
      console.error('Error loading lead stages:', error);
      showSnackbar('Failed to load lead stages', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleAddLeadStage = () => {
    setEditingLeadStage(null);
    setAddDialogOpen(true);
  };

  const handleEditLeadStage = (leadStage) => {
    setEditingLeadStage(leadStage);
    setEditDialogOpen(true);
  };

  const handleViewLeadStage = (leadStage) => {
    setSelectedLeadStage(leadStage);
    if (isMobile) {
      setMobileDrawerOpen(false); // Close drawer on mobile when lead stage is selected
    }
  };

  const handleDeleteLeadStage = async (leadStage) => {
    if (window.confirm(`Are you sure you want to delete the lead stage "${leadStage.name}"?`)) {
      try {
        await deleteLeadStage(leadStage._id);
        await loadLeadStages();
        if (selectedLeadStage && selectedLeadStage._id === leadStage._id) {
          setSelectedLeadStage(null);
        }
        showSnackbar('Lead stage deleted successfully');
      } catch (error) {
        console.error('Error deleting lead stage:', error);
        showSnackbar('Failed to delete lead stage', 'error');
      }
    }
  };

  const handleSaveLeadStage = async (leadStageData) => {
    try {
      if (editingLeadStage) {
        await updateLeadStage(editingLeadStage._id, leadStageData);
        showSnackbar('Lead stage updated successfully');
      } else {
        await createLeadStage(leadStageData);
        showSnackbar('Lead stage created successfully');
      }

      await loadLeadStages();
      setAddDialogOpen(false);
      setEditDialogOpen(false);
      setEditingLeadStage(null);
    } catch (error) {
      console.error('Error saving lead stage:', error);
      showSnackbar('Failed to save lead stage', 'error');
    }
  };

  const handleImportLeadStages = () => {
    setImportDialogOpen(true);
  };

  const handleImportComplete = (result) => {
    setImportDialogOpen(false);
    if (result.success) {
      loadLeadStages();
      showSnackbar('Lead stages imported successfully');
    } else {
      showSnackbar('Failed to import lead stages', 'error');
    }
  };

  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
    setEditingLeadStage(null);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditingLeadStage(null);
  };

  const leadStagesWithId = (leadStages || []).map((leadStage) => ({ ...leadStage, id: leadStage._id || leadStage.id }));

  // Mobile layout
  if (isMobile) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        {/* Mobile App Bar */}
        <AppBar position="static" elevation={1}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => setMobileDrawerOpen(true)}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" sx={{ flex: 1 }}>
              Lead Stages
            </Typography>
            <Button
              color="inherit"
              startIcon={<AddIcon />}
              onClick={handleAddLeadStage}
            >
              Add
            </Button>
          </Toolbar>
        </AppBar>

        {/* Main Content */}
        <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
          {selectedLeadStage ? (
            // Lead Stage Detail View
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Paper elevation={0} sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                  <LeadStageDetail
                    leadStage={selectedLeadStage}
                    onEdit={handleEditLeadStage}
                    onDelete={handleDeleteLeadStage}
                  />
                </Box>
              </Paper>
            </Box>
          ) : (
            // Empty State
            <Box sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              p: 3,
            }}
            >
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Select a Lead Stage
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Choose a lead stage from the list to view details
              </Typography>
            </Box>
          )}
        </Box>

        {/* Mobile Drawer for Lead Stage List */}
        <Drawer
          anchor="left"
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          sx={{
            '& .MuiDrawer-paper': {
              width: isSmallMobile ? '100%' : 320,
              boxSizing: 'border-box',
            },
          }}
        >
          <Box sx={{
            p: 2, height: '100%', display: 'flex', flexDirection: 'column',
          }}
          >
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              <LeadStageList
                leadStages={leadStagesWithId}
                loading={loading}
                onAdd={handleAddLeadStage}
                onEdit={handleEditLeadStage}
                onDelete={handleDeleteLeadStage}
                onView={handleViewLeadStage}
                onImport={handleImportLeadStages}
                selectedLeadStage={selectedLeadStage}
              />
            </Box>
          </Box>
        </Drawer>

        {/* Add Lead Stage Dialog */}
        <Dialog
          open={addDialogOpen}
          onClose={handleCloseAddDialog}
          maxWidth="md"
          fullWidth
          fullScreen={isSmallMobile}
          PaperProps={{
            sx: {
              maxHeight: isSmallMobile ? '100%' : '90vh',
              borderRadius: isSmallMobile ? 0 : 3,
              p: 0,
            },
          }}
        >
          <DialogTitle sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            py: 2,
            px: 3,
          }}
          >
            <AddIcon sx={{ fontSize: 32 }} />
            <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
              Add New Lead Stage
            </Typography>
            <IconButton onClick={handleCloseAddDialog} sx={{ color: 'primary.contrastText' }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <Box sx={{ p: { xs: 2, sm: 4 } }}>
            <LeadStageForm
              leadStage={null}
              onSave={handleSaveLeadStage}
              onCancel={handleCloseAddDialog}
              loading={loading}
            />
          </Box>
        </Dialog>

        {/* Edit Lead Stage Dialog */}
        <Dialog
          open={editDialogOpen}
          onClose={handleCloseEditDialog}
          maxWidth="md"
          fullWidth
          fullScreen={isSmallMobile}
          PaperProps={{
            sx: {
              maxHeight: isSmallMobile ? '100%' : '90vh',
              borderRadius: isSmallMobile ? 0 : 3,
              p: 0,
            },
          }}
        >
          <DialogTitle sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            py: 2,
            px: 3,
          }}
          >
            <AddIcon sx={{ fontSize: 32 }} />
            <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
              Edit Lead Stage
            </Typography>
            <IconButton onClick={handleCloseEditDialog} sx={{ color: 'primary.contrastText' }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <Box sx={{ p: { xs: 2, sm: 4 } }}>
            <LeadStageForm
              leadStage={editingLeadStage}
              onSave={handleSaveLeadStage}
              onCancel={handleCloseEditDialog}
              loading={loading}
            />
          </Box>
        </Dialog>

        {/* Import Dialog */}
        <Dialog
          open={importDialogOpen}
          onClose={() => setImportDialogOpen(false)}
          maxWidth="md"
          fullWidth
          fullScreen={isSmallMobile}
          PaperProps={{
            sx: {
              borderRadius: isSmallMobile ? 0 : 3,
              p: 0,
            },
          }}
        >
          <DialogTitle sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            py: 2,
            px: 3,
          }}
          >
            <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
              Import Lead Stages
            </Typography>
            <IconButton onClick={() => setImportDialogOpen(false)} sx={{ color: 'primary.contrastText' }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <Box sx={{ p: { xs: 2, sm: 4 } }}>
            <LeadStageImport
              onClose={() => setImportDialogOpen(false)}
              onImportComplete={handleImportComplete}
            />
          </Box>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  // Desktop layout
  return (
    <Box sx={{
      p: { xs: 1, sm: 2, md: 3 }, height: '100vh', display: 'flex', flexDirection: 'column',
    }}
    >
      <Typography variant="h4" component="h1" gutterBottom>
        Lead Stages
      </Typography>

      <Grid container spacing={3} sx={{ flex: 1, minHeight: 0 }}>
        {/* Lead Stage List */}
        <Grid item xs={12} md={selectedLeadStage ? 8 : 12}>
          <Paper sx={{
            height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0,
          }}
          >
            <LeadStageList
              leadStages={leadStagesWithId}
              loading={loading}
              onAdd={handleAddLeadStage}
              onEdit={handleEditLeadStage}
              onDelete={handleDeleteLeadStage}
              onView={handleViewLeadStage}
              onImport={handleImportLeadStages}
              selectedLeadStage={selectedLeadStage}
            />
          </Paper>
        </Grid>

        {/* Lead Stage Detail */}
        {selectedLeadStage && (
          <Grid item xs={12} md={4}>
            <Paper sx={{
              height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0,
            }}
            >
              <LeadStageDetail
                leadStage={selectedLeadStage}
                onEdit={handleEditLeadStage}
                onDelete={handleDeleteLeadStage}
              />
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Add Lead Stage Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={handleCloseAddDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { maxHeight: '90vh', borderRadius: 3, p: 0 },
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          py: 2,
          px: 3,
        }}
        >
          <AddIcon sx={{ fontSize: 32 }} />
          <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
            Add New Lead Stage
          </Typography>
          <IconButton onClick={handleCloseAddDialog} sx={{ color: 'primary.contrastText' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Box sx={{ p: { xs: 2, sm: 4 } }}>
          <LeadStageForm
            leadStage={null}
            onSave={handleSaveLeadStage}
            onCancel={handleCloseAddDialog}
            loading={loading}
          />
        </Box>
      </Dialog>

      {/* Edit Lead Stage Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { maxHeight: '90vh', borderRadius: 3, p: 0 },
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          py: 2,
          px: 3,
        }}
        >
          <AddIcon sx={{ fontSize: 32 }} />
          <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
            Edit Lead Stage
          </Typography>
          <IconButton onClick={handleCloseEditDialog} sx={{ color: 'primary.contrastText' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Box sx={{ p: { xs: 2, sm: 4 } }}>
          <LeadStageForm
            leadStage={editingLeadStage}
            onSave={handleSaveLeadStage}
            onCancel={handleCloseEditDialog}
            loading={loading}
          />
        </Box>
      </Dialog>

      {/* Import Dialog */}
      <Dialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, p: 0 },
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          py: 2,
          px: 3,
        }}
        >
          <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
            Import Lead Stages
          </Typography>
          <IconButton onClick={() => setImportDialogOpen(false)} sx={{ color: 'primary.contrastText' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Box sx={{ p: { xs: 2, sm: 4 } }}>
          <LeadStageImport
            onClose={() => setImportDialogOpen(false)}
            onImportComplete={handleImportComplete}
          />
        </Box>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default LeadStagesPage;
